import { RecurrencePattern, WarehouseGeneration } from "../../types";
import { z } from "zod";

export type EventType =
  | "custom"
  | "recommended"
  | "block"
  | "deleted"
  | "default"
  | "auto_resize"
  | "backoff";

export type ScheduleStatus = "pending" | "approved" | "rejected";

/**
 * Query Acceleration is tri-state, not a checkbox: a schedule may turn it on, turn it
 * off, or say nothing about it. "Unchanged" is the default for every schedule that
 * predates this control, so it must stay expressible.
 */
export enum QueryAccelerationAction {
  Unchanged = "unchanged",
  Enable = "enable",
  Disable = "disable",
}

export const queryAccelerationToPayload: Record<QueryAccelerationAction, boolean | undefined> = {
  [QueryAccelerationAction.Unchanged]: undefined,
  [QueryAccelerationAction.Enable]: true,
  [QueryAccelerationAction.Disable]: false,
};

export const queryAccelerationFromValue = (
  value: boolean | null | undefined
): QueryAccelerationAction => {
  if (value === true) return QueryAccelerationAction.Enable;
  if (value === false) return QueryAccelerationAction.Disable;
  return QueryAccelerationAction.Unchanged;
};

export interface ScheduleCard {
  id: string;
  // The backing schedule's numeric id. Absent on synthetic cards (the
  // `default-…` filler blocks), which have no schedule row behind them.
  scheduleId?: number;
  title: string;
  start: string;
  end: string;
  type: EventType;
  clusterMin?: number;
  clusterMax?: number;
  sizeFrom?: string;
  sizeTo?: string;
  queryAcceleration?: boolean | null;
  // Target warehouse generation for the window; null/undefined leaves it alone.
  generationTo?: WarehouseGeneration | null;
  // The warehouse's current generation, when the warehouse details report one.
  generationFrom?: WarehouseGeneration | null;
  // Latest end among the approved generation windows before this one, and the
  // target of the one ending exactly as it starts; see resolveGenerationFrom.
  generationPredecessorEnd?: string | null;
  generationHandOver?: WarehouseGeneration | null;
  savings?: string;
  // Savings range bounds, when the recommendation carries one.
  savingsMin?: string;
  savingsMax?: string;
  reasoning?: string[] | { timestamp: string; reason: string }[];
  status: ScheduleStatus;
  recurrencePattern?: RecurrencePattern;
  created_by?: number;
  change: "upsize" | "downsize" | "no_change" | "daybreak";
  // Non-null when this occurrence belongs to a recurring series.
  seriesId?: number | null;
  // The owning series' own IANA timezone, resolved server-side at read
  // time. Null/undefined for a standalone schedule, a deleted series, or a
  // backend that hasn't started returning it yet.
  seriesTimezone?: string | null;
}

// Define Zod schemas for request and response
export const ApplyRecommendedEventsRequestSchema = z.object({
  warehouse_name: z.string(),
  instance_id: z.string(),
  start_time: z.string(), // Assuming ISO string format
  end_time: z.string(), // Assuming ISO string format
});

export const ApproveScheduleResponseSchema = z.object({
  schedule_id: z.number(),
  warehouse_name: z.string(),
  configuration: z.any(), // Assuming WarehouseConfig is any
  start_time: z.string(), // Assuming ISO string format
  end_time: z.string(), // Assuming ISO string format
  schedule_type: z.string(), // Assuming ScheduleType is a string
  status: z.string(), // Assuming ScheduleStatus is a string
  reason: z.string(),
  created_by: z.number(),
  created_at: z.string(), // Assuming ISO string format
  updated_by: z.number().nullable(),
  updated_at: z.string().nullable(), // Assuming ISO string format
  recurrence_pattern: z.any().nullable(),
});

// Mirrors the backend contract for POST /warehouse/schedules/bulk-approve
// (altimate-backend app/schemas/warehouse_schedules.py: ScheduleConflictInfo /
// SkippedScheduleResponse / BulkApprovalResponse).
export const ScheduleTypeSchema = z.enum([
  "recommendation",
  "manual",
  "block",
  "auto_resize",
  "backoff",
]);

// `conflict_type` is ConflictType on the backend plus "error" for a schedule
// skipped due to a failure rather than a validator conflict.
export const ConflictTypeSchema = z.enum(["overlap", "invalid_time", "block", "error"]);

export const ScheduleConflictInfoSchema = z.object({
  conflict_type: ConflictTypeSchema,
  conflicting_schedule_id: z.number().nullable(),
  conflicting_schedule_type: ScheduleTypeSchema.nullable(),
  conflicting_start_time: z.string().nullable(),
  conflicting_end_time: z.string().nullable(),
  message: z.string(),
});

export const SkippedScheduleSchema = z.object({
  schedule_id: z.number(),
  schedule_type: ScheduleTypeSchema,
  start_time: z.string(),
  end_time: z.string(),
  // Backend guarantees at least one entry (a real conflict or a synthetic
  // "error" entry), so the UI can always show a reason.
  conflicts: z.array(ScheduleConflictInfoSchema).min(1),
});

export const BulkApprovalResponseSchema = z.object({
  approved: z.array(ApproveScheduleResponseSchema),
  skipped: z.array(SkippedScheduleSchema),
});
